import React, { useCallback, useContext, useMemo, useState } from 'react';

import { LayerContext } from 'state/LayerContext';
import { useGetSegmentInfoQuery } from 'state/apiSlice';
import { useAppSelector } from 'state/hooks';
import { TempRoute, selectCurrentSavedRoutes } from 'state/savedRouteSlices';

import { useRouteInfo } from 'features/common/hooks/useRouteInfo';
import { getIncrementalName } from 'features/common/utils';

import { AccordionWrapper } from './AccordionWrapper';
import { RouteRow } from './RouteRow';
import {
  useRMSegmentDataProvider,
  useSegmentRealtimeRMDataProvider,
} from './hooks/useRMDataProvider';

export function TemporaryRoute() {
  const { layer } = useContext(LayerContext);

  // Fetches stats for a temp route
  useRMSegmentDataProvider();
  useSegmentRealtimeRMDataProvider();

  const { basicRouteInfo, feature: selectionFeature } = useRouteInfo();
  const savedRoutes = useAppSelector(selectCurrentSavedRoutes);

  const [open, setOpen] = useState<boolean>(false);

  const { currentData: segmentData } = useGetSegmentInfoQuery(
    { layerId: layer, routeIds: basicRouteInfo.realizedSegments },
    {
      skip:
        !layer ||
        !basicRouteInfo ||
        basicRouteInfo.isSavedRoute ||
        !(basicRouteInfo.realizedSegments?.length > 0),
    },
  );

  const existingNames = useMemo(
    () =>
      savedRoutes?.map((r) => r.savedRoute.routeName.trim().toLowerCase()) ||
      [],
    [savedRoutes],
  );

  const segmentName = useMemo(
    () =>
      basicRouteInfo.realizedSegments?.[0] != null
        ? segmentData?.segments[basicRouteInfo.realizedSegments[0]]?.properties
            ?.name
        : undefined,
    [basicRouteInfo.realizedSegments, segmentData],
  );

  const getRouteName = useCallback(
    (name: string): string => {
      let trimmed = name.trim();
      while (existingNames.includes(trimmed.toLowerCase()))
        trimmed = getIncrementalName(trimmed);
      return trimmed;
    },
    [existingNames],
  );

  const tempRoute: TempRoute = useMemo(
    () => ({
      id: 'temp',
      routeName: getRouteName(segmentName || ''),
      cardinal_direction: selectionFeature?.properties?.cardinal_direction,
      route_length: selectionFeature?.properties?.route_length,
    }),
    [segmentName, getRouteName, selectionFeature],
  );

  const toggleOpen = () => {
    setOpen(!open);
  };

  return (
    <AccordionWrapper allowHide={false} title="Draft">
      <RouteRow
        disableOrdering
        route={tempRoute}
        onSelect={toggleOpen}
        isOpen={open}
      />
    </AccordionWrapper>
  );
}
